import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  Slide,
  Avatar,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
  EditOutlined as EditOutlinedIcon,
  StickyNote2Outlined as NoteIcon,
  CheckRounded as CheckIcon,
  CloseRounded as CancelIcon,
  NoteAltOutlined as NoteAltIcon,
  TrendingUpOutlined as ProgressIcon
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import getInitials from "../../utils/getInitials";
import useSignedUrl from "../../utils/useSignedUrl";
import {
  getClientNotes,
  addClientNote,
  updateClientNote,
  deleteClientNote
} from "../../api/clientNotesApi";

dayjs.extend(utc);

const NOTE_MAX = 300;
const PROGRESS_MAX = 300;

const PRIMARY = "#1976d2";
const PRIMARY_DARK = "#1565c0";
const PRIMARY_LIGHT = "#E3F2FD";
const PRIMARY_BORDER = "#BBDEFB";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ─── Avatar ───────────────────────────────────────────────────────────────────
const UserAvatar = ({ user, size = 40 }) => {
  const imgSrc = useSignedUrl(
    user?.profilePic,
    process.env.REACT_APP_BACKEND_PROFILE_IMAGE_LOCATION
  );
  if (user?.profilePic && imgSrc) {
    return (
      <Box
        component="img"
        src={imgSrc}
        alt="avatar"
        sx={{
          width: size,
          height: size,
          borderRadius: "50%",
          objectFit: "cover",
          border: "2px solid rgba(255,255,255,0.3)",
          flexShrink: 0
        }}
      />
    );
  }
  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        bgcolor: PRIMARY_LIGHT,
        color: PRIMARY,
        fontSize: size * 0.36,
        fontWeight: 700,
        border: `1.5px solid ${PRIMARY_BORDER}`,
        flexShrink: 0
      }}
    >
      {getInitials(user?.fullName || user?.displayName || "?")}
    </Avatar>
  );
};

// ─── Empty State ──────────────────────────────────────────────────────────────
const EmptyState = ({ onAdd }) => (
  <TableRow>
    <TableCell colSpan={4} sx={{ border: 0, py: 8, backgroundColor: "#fff" }}>
      <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: "16px",
            backgroundColor: PRIMARY_LIGHT,
            border: `1px solid ${PRIMARY_BORDER}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <NoteAltIcon sx={{ fontSize: 28, color: PRIMARY }} />
        </Box>
        <Typography sx={{ color: "#111827", fontSize: 15, fontWeight: 600 }}>
          No notes yet
        </Typography>
        <Typography sx={{ color: "#6B7280", fontSize: 13, textAlign: "center", maxWidth: 260, lineHeight: 1.6 }}>
          Start tracking this client's progress by adding the first note.
        </Typography>
        <Button
          onClick={onAdd}
          startIcon={<AddIcon />}
          variant="outlined"
          disableElevation
          sx={{
            mt: 1,
            color: PRIMARY,
            borderColor: PRIMARY,
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13.5,
            borderRadius: "10px",
            px: 3,
            py: 0.9,
            "&:hover": { backgroundColor: PRIMARY_LIGHT, borderColor: PRIMARY_DARK }
          }}
        >
          Add First Note
        </Button>
      </Box>
    </TableCell>
  </TableRow>
);

// ─── Table Row (note) ─────────────────────────────────────────────────────────
const NoteTableRow = ({ note, index, onEdit, onDelete, isDeleting }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isEven = index % 2 === 0;

  return (
    <Fade in timeout={200}>
      <TableRow
        sx={{
          backgroundColor: isEven ? "#fff" : "#FAFAFA",
          "&:hover": { backgroundColor: PRIMARY_LIGHT },
          transition: "background-color 0.15s ease"
        }}
      >
        {/* Date */}
        <TableCell sx={{ width: 120, minWidth: 120, maxWidth: 120, py: 2, px: 2, borderBottom: "1px solid #F3F4F6", borderRight: "1px solid #F3F4F6", whiteSpace: "nowrap" }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: PRIMARY }}>
            {dayjs.utc(note.date).format("MMM DD, YYYY")}
          </Typography>
        </TableCell>

        {/* Notes */}
        <TableCell sx={{ py: 2, px: 2, borderBottom: "1px solid #F3F4F6", borderRight: "1px solid #F3F4F6", maxWidth: 260 }}>
          {note.notes
            ? <Typography sx={{ fontSize: 13.5, color: "#374151", lineHeight: 1.65, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {note.notes}
              </Typography>
            : <Typography sx={{ fontSize: 13, color: "#D1D5DB", fontStyle: "italic" }}>—</Typography>
          }
        </TableCell>

        {/* Progress */}
        <TableCell sx={{ py: 2, px: 2, borderBottom: "1px solid #F3F4F6", borderRight: "1px solid #F3F4F6", maxWidth: 260 }}>
          {note.progress
            ? <Typography sx={{ fontSize: 13.5, color: "#374151", lineHeight: 1.65, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {note.progress}
              </Typography>
            : <Typography sx={{ fontSize: 13, color: "#D1D5DB", fontStyle: "italic" }}>—</Typography>
          }
        </TableCell>

        {/* Actions */}
        <TableCell align="center" sx={{ width: 90, py: 2, px: 2, borderBottom: "1px solid #F3F4F6" }}>
          {!confirmDelete ? (
            <Box display="flex" gap={0.8} justifyContent="center">
              <Tooltip title="Edit" placement="top">
                <IconButton
                  size="small"
                  onClick={() => onEdit(note)}
                  sx={{
                    width: 30, height: 30,
                    backgroundColor: "#fff",
                    borderRadius: "7px",
                    border: `1px solid ${PRIMARY_BORDER}`,
                    "&:hover": { backgroundColor: PRIMARY, borderColor: PRIMARY, "& svg": { color: "#fff" } }
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 13, color: PRIMARY }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete" placement="top">
                <IconButton
                  size="small"
                  onClick={() => setConfirmDelete(true)}
                  sx={{
                    width: 30, height: 30,
                    backgroundColor: "#fff",
                    borderRadius: "7px",
                    border: "1px solid #FEE2E2",
                    "&:hover": { backgroundColor: "#DC2626", borderColor: "#DC2626", "& svg": { color: "#fff" } }
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 13, color: "#DC2626" }} />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" alignItems="center" gap={0.6}>
              <Typography sx={{ fontSize: 10.5, color: "#DC2626", fontWeight: 700, whiteSpace: "nowrap" }}>
                Are you sure?
              </Typography>
              <Box display="flex" gap={0.5}>
                <Tooltip title="Yes, delete">
                  <IconButton
                    size="small"
                    onClick={() => { setConfirmDelete(false); onDelete(note._id); }}
                    disabled={isDeleting}
                    sx={{
                      width: 26, height: 26,
                      backgroundColor: "#DC2626",
                      borderRadius: "6px",
                      "&:hover": { backgroundColor: "#B91C1C" },
                      "&.Mui-disabled": { backgroundColor: "#FCA5A5" }
                    }}
                  >
                    {isDeleting
                      ? <CircularProgress size={10} sx={{ color: "#fff" }} />
                      : <CheckIcon sx={{ fontSize: 12, color: "#fff" }} />
                    }
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton
                    size="small"
                    onClick={() => setConfirmDelete(false)}
                    sx={{
                      width: 26, height: 26,
                      backgroundColor: "#F3F4F6",
                      borderRadius: "6px",
                      "&:hover": { backgroundColor: "#E5E7EB" }
                    }}
                  >
                    <CancelIcon sx={{ fontSize: 12, color: "#6B7280" }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}
        </TableCell>
      </TableRow>
    </Fade>
  );
};

// ─── Note Form ────────────────────────────────────────────────────────────────
const NoteForm = ({ initial, onSave, onCancel, saving }) => {
  const [date, setDate] = useState(
    initial?.date ? dayjs.utc(initial.date) : dayjs.utc()
  );
  const [notes, setNotes] = useState(initial?.notes || "");
  const [progress, setProgress] = useState(initial?.progress || "");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setDate(initial?.date ? dayjs.utc(initial.date) : dayjs.utc());
    setNotes(initial?.notes || "");
    setProgress(initial?.progress || "");
    setErrors({});
  }, [initial?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const validate = () => {
    const errs = {};
    if (!date || !date.isValid()) errs.date = "Please select a valid date.";
    if (!notes.trim() && !progress.trim())
      errs.notes = "At least one of Notes or Progress is required.";
    if (notes.length > NOTE_MAX)
      errs.notes = `Notes must be ${NOTE_MAX} characters or less.`;
    if (progress.length > PROGRESS_MAX)
      errs.progress = `Progress must be ${PROGRESS_MAX} characters or less.`;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSave({ date: date.format("YYYY-MM-DD"), notes: notes.trim(), progress: progress.trim() });
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#fff",
      fontSize: 13.5,
      "& fieldset": { borderColor: "#E5E7EB" },
      "&:hover fieldset": { borderColor: "#9CA3AF" },
      "&.Mui-focused fieldset": { borderColor: PRIMARY, borderWidth: "1.5px" }
    },
    "& .MuiInputLabel-root": { fontSize: 13.5, color: "#9CA3AF" },
    "& .MuiInputLabel-root.Mui-focused": { color: PRIMARY },
    "& .MuiFormHelperText-root": { fontSize: 11, mx: 0, mt: 0.5 }
  };

  return (
    <Fade in timeout={200}>
      <Box
        sx={{
          borderRadius: "12px",
          border: `1.5px solid ${PRIMARY}`,
          backgroundColor: "#fff",
          overflow: "hidden",
          mb: 2,
          boxShadow: `0 2px 12px rgba(25,118,210,0.1)`
        }}
      >
        {/* Form header */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            backgroundColor: PRIMARY,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>
            {initial ? "Edit Note" : "New Note"}
          </Typography>
          <IconButton
            size="small"
            onClick={onCancel}
            disabled={saving}
            sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff" }, p: 0.4 }}
          >
            <CloseIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Box>

        {/* Fields — 3-column grid matching table */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "160px 1fr 1fr",
            gap: 2,
            p: 2.5,
            alignItems: "flex-start"
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date"
              value={date}
              onChange={(v) => setDate(v)}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  error: !!errors.date,
                  helperText: errors.date,
                  sx: fieldSx
                }
              }}
            />
          </LocalizationProvider>

          <TextField
            label="Notes"
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            size="small"
            value={notes}
            onChange={(e) => { if (e.target.value.length <= NOTE_MAX) setNotes(e.target.value); }}
            error={!!errors.notes}
            helperText={
              errors.notes
                ? <span style={{ color: "#DC2626" }}>{errors.notes}</span>
                : <span style={{ color: notes.length > NOTE_MAX * 0.85 ? "#F59E0B" : "#9CA3AF" }}>
                    {notes.length} / {NOTE_MAX}
                  </span>
            }
            placeholder="Coach notes about this client…"
            sx={fieldSx}
          />

          <TextField
            label="Progress"
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            size="small"
            value={progress}
            onChange={(e) => { if (e.target.value.length <= PROGRESS_MAX) setProgress(e.target.value); }}
            error={!!errors.progress}
            helperText={
              errors.progress
                ? <span style={{ color: "#DC2626" }}>{errors.progress}</span>
                : <span style={{ color: progress.length > PROGRESS_MAX * 0.85 ? "#F59E0B" : "#9CA3AF" }}>
                    {progress.length} / {PROGRESS_MAX}
                  </span>
            }
            placeholder="Describe client's progress…"
            sx={fieldSx}
          />
        </Box>

        {/* Buttons */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, px: 2.5, pb: 2.5 }}>
          <Button
            onClick={onCancel}
            disabled={saving}
            disableElevation
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              color: "#6B7280",
              borderRadius: "8px",
              px: 2.5,
              py: 0.9,
              border: "1px solid #E5E7EB",
              backgroundColor: "#fff",
              "&:hover": { backgroundColor: "#F9FAFB" }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving}
            variant="contained"
            disableElevation
            startIcon={saving ? <CircularProgress size={12} sx={{ color: "rgba(255,255,255,0.7)" }} /> : null}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              borderRadius: "8px",
              px: 3,
              py: 0.9,
              backgroundColor: PRIMARY,
              "&:hover": { backgroundColor: PRIMARY_DARK },
              "&.Mui-disabled": { backgroundColor: PRIMARY_BORDER, color: "#fff" }
            }}
          >
            {saving ? "Saving…" : initial ? "Update Note" : "Save Note"}
          </Button>
        </Box>
      </Box>
    </Fade>
  );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────
const ClientNotesModal = ({ open, onClose, user }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const scrollRef = React.useRef(null);

  const userId = user?.userId;
  const userName = user?.fullName || user?.displayName || "Client";

  const fetchNotes = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await getClientNotes(userId);
      const sorted = (data?.notes || []).sort((a, b) => new Date(b.date) - new Date(a.date));
      setNotes(sorted);
    } catch {
      setSnackbar({ open: true, message: "Failed to load notes.", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (open && userId) {
      fetchNotes();
      setShowForm(false);
      setEditingNote(null);
    }
  }, [open, userId, fetchNotes]);

  const scrollToTop = () => {
    setTimeout(() => scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  const handleSave = async ({ date, notes: noteText, progress }) => {
    setSaving(true);
    try {
      if (editingNote) {
        await updateClientNote({ noteId: editingNote._id, date, notes: noteText, progress });
        setSnackbar({ open: true, message: "Note updated successfully.", severity: "success" });
      } else {
        await addClientNote({ userId, date, notes: noteText, progress });
        setSnackbar({ open: true, message: "Note added successfully.", severity: "success" });
      }
      setShowForm(false);
      setEditingNote(null);
      await fetchNotes();
    } catch {
      setSnackbar({ open: true, message: "Failed to save note.", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (note) => {
    setEditingNote(note);
    setShowForm(true);
    scrollToTop();
  };

  const handleDelete = async (noteId) => {
    setDeletingId(noteId);
    try {
      await deleteClientNote(noteId);
      setSnackbar({ open: true, message: "Note deleted.", severity: "success" });
      await fetchNotes();
    } catch {
      setSnackbar({ open: true, message: "Failed to delete note.", severity: "error" });
    } finally {
      setDeletingId(null);
    }
  };

  const openNewForm = () => {
    setEditingNote(null);
    setShowForm(true);
    scrollToTop();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        TransitionComponent={Transition}
        maxWidth="md"
        fullWidth
        disableAutoFocus
        disableEnforceFocus
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: "16px",
            maxHeight: "88vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            border: `1px solid ${PRIMARY_BORDER}`,
            boxShadow: "0 20px 60px rgba(0,0,0,0.12)"
          }
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            px: 3,
            py: 2,
            flexShrink: 0,
            borderBottom: `1px solid ${PRIMARY_BORDER}`,
            display: "flex",
            alignItems: "center",
            gap: 2,
            backgroundColor: "#fff"
          }}
        >
          {/* Avatar */}
          <UserAvatar user={user} size={40} />

          {/* Name + subtitle */}
          <Box flex={1} minWidth={0}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#111827",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}
              >
                {userName}
              </Typography>
              <Box
                sx={{
                  flexShrink: 0,
                  backgroundColor: PRIMARY_LIGHT,
                  border: `1px solid ${PRIMARY_BORDER}`,
                  borderRadius: "6px",
                  px: 1,
                  py: 0.2
                }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: PRIMARY }}>
                  {notes.length} {notes.length === 1 ? "note" : "notes"}
                </Typography>
              </Box>
            </Box>
            <Typography
              sx={{
                fontSize: 12,
                color: "#9CA3AF",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {user?.email}
            </Typography>
          </Box>

          {/* Right actions */}
          <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
            {!showForm && (
              <Button
                onClick={openNewForm}
                startIcon={<AddIcon sx={{ fontSize: "15px !important" }} />}
                disableElevation
                size="small"
                variant="contained"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "8px",
                  px: 2,
                  py: 0.7,
                  backgroundColor: PRIMARY,
                  boxShadow: "none",
                  "&:hover": { backgroundColor: PRIMARY_DARK }
                }}
              >
                Add Note
              </Button>
            )}
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                width: 32,
                height: 32,
                color: "#9CA3AF",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                "&:hover": { color: "#374151", backgroundColor: "#F9FAFB" }
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>

        {/* ── Scrollable Body ── */}
        <Box
          ref={scrollRef}
          sx={{
            flex: 1,
            overflowY: "auto",
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": { backgroundColor: PRIMARY_BORDER, borderRadius: "10px" }
          }}
        >
          {/* Inline form */}
          {showForm && (
            <Box sx={{ p: 2.5, pb: 0 }}>
              <NoteForm
                initial={editingNote}
                onSave={handleSave}
                onCancel={() => { setShowForm(false); setEditingNote(null); }}
                saving={saving}
              />
            </Box>
          )}

          {/* Table */}
          <TableContainer sx={{ flex: 1 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    { label: "Date", width: 120, minWidth: 120, maxWidth: 120 },
                    { label: "Notes", width: "auto" },
                    { label: "Progress", width: "auto" },
                    { label: "Actions", width: 90, minWidth: 90, align: "center" }
                  ].map((col) => (
                    <TableCell
                      key={col.label}
                      align={col.align || "left"}
                      sx={{
                        width: col.width,
                        minWidth: col.minWidth,
                        maxWidth: col.maxWidth,
                        backgroundColor: PRIMARY,
                        color: "#fff",
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        textTransform: "uppercase",
                        py: 1.3,
                        px: 2,
                        borderBottom: "none",
                        borderRight: col.label !== "Actions" ? "1px solid rgba(255,255,255,0.15)" : "none",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {col.label === "Notes" ? (
                        <Box display="flex" alignItems="center" gap={0.6}>
                          <NoteIcon sx={{ fontSize: 13, color: "rgba(255,255,255,0.75)" }} />
                          {col.label}
                        </Box>
                      ) : col.label === "Progress" ? (
                        <Box display="flex" alignItems="center" gap={0.6}>
                          <ProgressIcon sx={{ fontSize: 13, color: "rgba(255,255,255,0.75)" }} />
                          {col.label}
                        </Box>
                      ) : col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ border: 0, py: 8, backgroundColor: "#fff" }}>
                      <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                        <CircularProgress size={30} thickness={3} sx={{ color: PRIMARY }} />
                        <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>Loading notes…</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : notes.length === 0 && !showForm ? (
                  <EmptyState onAdd={openNewForm} />
                ) : (
                  notes.map((note, index) => (
                    <NoteTableRow
                      key={note._id}
                      note={note}
                      index={index}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      isDeleting={deletingId === note._id}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: "12px", fontWeight: 600, fontSize: 13.5 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ClientNotesModal;
